/**
 * HTML sanitizer using DOMPurify to prevent XSS attacks
 * Uses a well-tested, secure library instead of custom implementation
 */

import DOMPurify from 'dompurify';

// DOMPurify configuration for slide content
const SANITIZE_CONFIG = {
  // Allow these tags for slide formatting
  ALLOWED_TAGS: [
    'b', 'i', 'u', 'strong', 'em', 'br', 'p', 'div', 'span', 
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
    'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
    'img', 'a', 'table', 'thead', 'tbody', 'tr', 'td', 'th'
  ],
  
  // Allow these attributes
  ALLOWED_ATTR: [
    'class', 'style', 'id', 'data-*',
    'href', 'src', 'alt', 'title',
    'width', 'height', 'colspan', 'rowspan'
  ],
  
  // Allow data URIs for images but prevent XSS
  ALLOW_DATA_ATTR: true,
  
  // Remove script-related content
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  
  // Keep whitespace and formatting
  KEEP_CONTENT: true,
  
  // Return DOM instead of string for better performance
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  
  // Additional security measures
  SANITIZE_DOM: true,
  WHOLE_DOCUMENT: false,
  
  // Custom hook to remove dangerous CSS
  ADD_ATTR: ['target'],
  
  // Allow safe CSS properties
  ALLOW_UNKNOWN_PROTOCOLS: false
};

/**
 * Sanitize HTML content using DOMPurify
 * @param html - Raw HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    // Use DOMPurify with our configuration
    const sanitized = DOMPurify.sanitize(html, SANITIZE_CONFIG);
    
    // Additional custom cleaning for slide-specific security
    return sanitized
      .replace(/javascript:/gi, '') // Remove any remaining javascript: protocols
      .replace(/data:text\/html/gi, '') // Remove HTML data URIs
      .replace(/vbscript:/gi, '') // Remove vbscript protocols
      .replace(/on\w+\s*=/gi, ''); // Remove any remaining event handlers
      
  } catch (error) {
    console.warn('HTML sanitization failed:', error);
    // Fallback: escape HTML entirely if sanitization fails
    return escapeHtml(html);
  }
}

/**
 * Sanitize HTML for slide content with stricter rules
 * Used for user-generated content in slides
 */
export function sanitizeSlideContent(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const strictConfig = {
    ...SANITIZE_CONFIG,
    // More restrictive for slide content
    ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'span'],
    ALLOWED_ATTR: ['class', 'style'],
    FORBID_TAGS: [...SANITIZE_CONFIG.FORBID_TAGS, 'img', 'a', 'iframe'],
  };

  try {
    return DOMPurify.sanitize(html, strictConfig);
  } catch (error) {
    console.warn('Slide content sanitization failed:', error);
    return escapeHtml(html);
  }
}

/**
 * Escape HTML entities as fallback
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Convert newlines to <br> tags safely
 * @param text - Plain text with newlines
 * @returns HTML with <br> tags, properly escaped
 */
export function nl2br(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // First escape HTML to prevent injection
  const escaped = escapeHtml(text);
  
  // Then convert newlines to <br>
  return escaped.replace(/\n/g, '<br>');
}

/**
 * Strip all HTML tags and return plain text
 * @param html - HTML string
 * @returns Plain text content
 */
export function stripHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    // Use DOMPurify to remove all tags
    return DOMPurify.sanitize(html, { 
      ALLOWED_TAGS: [], 
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true 
    });
  } catch (error) {
    console.warn('HTML stripping failed:', error);
    // Fallback: use DOM to extract text content
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }
}

/**
 * Validate if HTML is safe after sanitization
 * @param original - Original HTML
 * @param sanitized - Sanitized HTML
 * @returns boolean indicating if content was significantly modified
 */
export function validateSanitization(original: string, sanitized: string): boolean {
  // Check if sanitization removed significant content
  const originalLength = original.length;
  const sanitizedLength = sanitized.length;
  
  // If more than 50% of content was removed, it might indicate malicious content
  const reductionRatio = (originalLength - sanitizedLength) / originalLength;
  
  if (reductionRatio > 0.5) {
    console.warn('HTML sanitization removed significant content, possible XSS attempt');
    return false;
  }
  
  return true;
}

// Initialize DOMPurify with additional hooks for security
if (typeof window !== 'undefined') {
  // Add a hook to further validate URLs
  DOMPurify.addHook('beforeSanitizeAttributes', (node) => {
    // Remove any attributes that contain suspicious content
    if (node.hasAttribute && typeof node.hasAttribute === 'function') {
      const attributes = node.attributes;
      if (attributes) {
        for (let i = attributes.length - 1; i >= 0; i--) {
          const attr = attributes[i];
          if (attr.value && (
            attr.value.includes('javascript:') ||
            attr.value.includes('data:text/html') ||
            attr.value.includes('vbscript:') ||
            /on\w+/.test(attr.name)
          )) {
            node.removeAttribute(attr.name);
          }
        }
      }
    }
  });
}